'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from 'next/image';
import { CategoryIntentLink as Link, CategoryIntentProvider } from './category-intent-link';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePathname } from 'next/navigation';

import { RxDashboard } from 'react-icons/rx';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { LuFactory } from 'react-icons/lu';
import { Button } from '../ui/button';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { ButtonIcon } from '../ui/button-icon';
import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import { useHeaderCategoryChildren } from '../../hooks/categories/use-header-category-children';

const CATEGORY_HOVER_INTENT_MS = 150;

function hasCategoryChildren(category: CategoriesEntity | null): boolean {
  return Boolean(category && (category.has_childs ?? category.childs?.length));
}

function shouldExpandOnTouch(hasChildren: boolean, isHovered: boolean): boolean {
  return hasChildren && !isHovered && window.matchMedia('(hover: none), (pointer: coarse)').matches;
}

export function HeaderCategoriesButton({
  categories,
  locale,
}: {
  categories: CategoriesEntity[];
  locale: string;
}) {
  const pathname = usePathname();
  const { isOpen, setIsOpen } = useDisclosure(false);

  const [hoveredCategory, setHoveredCategory] = useState<CategoriesEntity | null>(null);
  const [hoveredSubCategoryItem, setHoveredSubCategoryItem] = useState<CategoriesEntity | null>(
    null,
  );
  const [navigatingTo, setNavigatingTo] = useState<string | null>(null);
  const hoverIntentRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const hoveredCategoryHasChildren = hasCategoryChildren(hoveredCategory);
  const hoveredCategoryChildrenQuery = useHeaderCategoryChildren({
    locale,
    parentId: hoveredCategory?.id,
    enabled: isOpen && hoveredCategoryHasChildren,
  });
  const hoveredCategoryChildren = hoveredCategoryChildrenQuery.data ?? [];

  const hoveredSubCategoryHasChildren = hasCategoryChildren(hoveredSubCategoryItem);
  const hoveredSubCategoryChildrenQuery = useHeaderCategoryChildren({
    locale,
    parentId: hoveredSubCategoryItem?.id,
    enabled: isOpen && hoveredSubCategoryHasChildren,
  });
  const hoveredSubCategoryChildren = hoveredSubCategoryChildrenQuery.data ?? [];

  const clearHoverIntent = useCallback(() => {
    if (!hoverIntentRef.current) return;
    clearTimeout(hoverIntentRef.current);
    hoverIntentRef.current = null;
  }, []);

  const showCategory = (category: CategoriesEntity) => {
    clearHoverIntent();
    setHoveredCategory(category);
    setHoveredSubCategoryItem(null);
  };

  const showSubCategory = (category: CategoriesEntity) => {
    clearHoverIntent();
    setHoveredSubCategoryItem(category);
  };

  const scheduleCategory = (category: CategoriesEntity) => {
    clearHoverIntent();
    setHoveredSubCategoryItem(null);
    hoverIntentRef.current = setTimeout(() => showCategory(category), CATEGORY_HOVER_INTENT_MS);
  };

  const scheduleSubCategory = (category: CategoriesEntity) => {
    clearHoverIntent();
    hoverIntentRef.current = setTimeout(() => showSubCategory(category), CATEGORY_HOVER_INTENT_MS);
  };

  const closeMenu = useCallback((restoreTriggerFocus = false) => {
    clearHoverIntent();
    setIsOpen(false);
    setNavigatingTo(null);
    setHoveredCategory(null);
    setHoveredSubCategoryItem(null);
    if (restoreTriggerFocus) requestAnimationFrame(() => triggerRef.current?.focus());
  }, [clearHoverIntent, setIsOpen]);

  useEffect(() => {
    closeMenu();
  }, [locale, pathname, closeMenu]);

  useEffect(() => {
    // Core emits this on a committed URL change, including query-only navigation.
    const handleComplete = () => {
      if (navigatingTo) closeMenu();
    };
    window.addEventListener('eliadmin:navigation-complete', handleComplete);
    return () => window.removeEventListener('eliadmin:navigation-complete', handleComplete);
  }, [navigatingTo, closeMenu]);

  useEffect(
    () => () => {
      if (hoverIntentRef.current) clearTimeout(hoverIntentRef.current);
    },
    [],
  );

  const handleNavigate = (href: string, e: React.MouseEvent) => {
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button !== 0) return;
    const target = new URL((e.currentTarget as HTMLAnchorElement).href, window.location.href);
    if (target.href === window.location.href) {
      closeMenu();
      return;
    }
    clearHoverIntent();
    setNavigatingTo(href);
    // Keep the current menu/content visible until Next commits the destination.
    // Links stay usable so a failed request never locks the menu.
  };

  const handleMenuLeave = () => {
    clearHoverIntent();
    if (navigatingTo) return;
    setHoveredCategory(null);
    setHoveredSubCategoryItem(null);
  };

  const manufacturersHref = ROUTES.MANUFACTURERS();

  return (
    <CategoryIntentProvider active={isOpen}>
      <Button
        ref={triggerRef}
        variant={'secondary'}
        className="whitespace-nowrap"
        aria-expanded={isOpen}
        aria-haspopup="menu"
        onClick={() => {
          if (isOpen) {
            closeMenu();
            return;
          }
          // Reopening permits a retry even if the previous route failed or was unchanged.
          setNavigatingTo(null);
          setIsOpen(true);
        }}
        onKeyDown={event => {
          if (!isOpen || event.key !== 'Escape') return;
          event.preventDefault();
          closeMenu(true);
        }}
      >
        <RxDashboard size={20} /> E-shop
      </Button>

      {isOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[39] bg-black/25 duration-200 animate-in fade-in"
            onClick={() => closeMenu()}
          />,
          document.body,
        )}
      {isOpen && (
        <>
          <div
            className={cn(
              'absolute left-1/2 top-full z-[20] h-[60dvh] -translate-x-1/2 overflow-hidden bg-white py-8',
              'custom-section',
              'duration-200 ease-out animate-in fade-in slide-in-from-top-2',
            )}
            onMouseLeave={handleMenuLeave}
            onKeyDown={event => {
              if (event.key !== 'Escape') return;
              event.preventDefault();
              closeMenu(true);
            }}
          >
            <div className={cn('custom-container', 'grid h-full min-h-0 grid-cols-[minmax(0,1.2fr)_minmax(0,1.67fr)_minmax(0,1fr)] gap-4')}>
              {/* Column 1: Main categories; the manufacturers link stays visible below the scroll */}
              <div className="flex min-h-0 min-w-0 flex-col">
                <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain [scrollbar-gutter:stable]">
                  {categories.map((category, idx) => {
                    // Categories without an icon (e.g. „Mobily a tablety“, „SMART produkty“) used to be
                    // skipped, so the desktop menu showed 7 of 9 top categories.
                    const href = ROUTES.CATEGORY({ slug: category.slug });
                    return (
                      <CategoryItem
                        key={category.id}
                        category={category}
                        href={href}
                        isHovered={hoveredCategory?.id === category.id}
                        isNavigating={navigatingTo === href}
                        animationDelay={idx * 40}
                        hasChildren={hasCategoryChildren(category)}
                        onHover={() => scheduleCategory(category)}
                        onFocus={() => showCategory(category)}
                        onNavigate={handleNavigate}
                      />
                    );
                  })}
                </div>
                <div className="mt-2 shrink-0 overflow-hidden border-t border-border pt-2 [scrollbar-gutter:stable]">
                  <Link
                    prefetch={false}
                    href={manufacturersHref}
                    className={cn(
                      'flex w-full items-center gap-2.5 rounded-full p-4 hover:bg-[#FFFACC]',
                      'transition-all duration-200 ease-in-out',
                      'animate-in fade-in slide-in-from-top-1',
                      navigatingTo === manufacturersHref && 'opacity-60',
                    )}
                    style={{
                      animationDelay: `${categories.length * 40}ms`,
                      animationFillMode: 'backwards',
                    }}
                    // Leaves the category columns, so no stale subcategories stay open beside it.
                    onMouseEnter={handleMenuLeave}
                    onFocus={handleMenuLeave}
                    onClick={e => handleNavigate(manufacturersHref, e)}
                  >
                    <LuFactory size={24} className="shrink-0" aria-hidden />
                    <span className="h6 w-full flex-1 font-semibold tracking-tight">Výrobcovia</span>
                    {navigatingTo === manufacturersHref && (
                      <AiOutlineLoading3Quarters
                        size={16}
                        className="shrink-0 animate-spin text-primary"
                      />
                    )}
                  </Link>
                </div>
              </div>

              {/* Column 2: Subcategories */}
              {hoveredCategoryHasChildren && (
                <>
                  <div
                    key={hoveredCategory?.id}
                    className={cn(
                      'flex min-h-0 min-w-0 flex-col gap-0 overflow-y-auto overscroll-contain border-l border-border pl-4 [scrollbar-gutter:stable]',
                      'ea-subcategories__container',
                      'duration-200 ease-out animate-in fade-in slide-in-from-left-3',
                    )}
                  >
                    {hoveredCategoryChildrenQuery.isPending && (
                      <CategoryLoadingState label="Načítavam podkategórie…" />
                    )}
                    {hoveredCategoryChildrenQuery.isError && (
                      <CategoryErrorState
                        onRetry={() => void hoveredCategoryChildrenQuery.refetch()}
                      />
                    )}
                    {hoveredCategoryChildren.map((subCategory, idx) => {
                      const href = ROUTES.CATEGORY({ slug: subCategory.slug });
                      return (
                        <SubcategoryItem
                          key={subCategory.id}
                          subCategory={subCategory}
                          href={href}
                          isHovered={hoveredSubCategoryItem?.id === subCategory.id}
                          isNavigating={navigatingTo === href}
                          animationDelay={idx * 40}
                          onHover={() => scheduleSubCategory(subCategory)}
                          onFocus={() => showSubCategory(subCategory)}
                          onNavigate={handleNavigate}
                        />
                      );
                    })}
                  </div>
                </>
              )}

              {/* Column 3: Grandchildren */}
              {hoveredSubCategoryHasChildren && (
                <>
                  <div
                    key={hoveredSubCategoryItem?.id}
                    className={cn(
                      'flex min-h-0 min-w-0 flex-col overflow-y-auto overscroll-contain border-l border-border pl-4 [scrollbar-gutter:stable]',
                      'duration-200 ease-out animate-in fade-in slide-in-from-left-3',
                    )}
                  >
                    {hoveredSubCategoryChildrenQuery.isPending && (
                      <CategoryLoadingState label="Načítavam ďalšie kategórie…" />
                    )}
                    {hoveredSubCategoryChildrenQuery.isError && (
                      <CategoryErrorState
                        onRetry={() => void hoveredSubCategoryChildrenQuery.refetch()}
                      />
                    )}
                    {hoveredSubCategoryChildren.map((grandChild, idx) => {
                      const href = ROUTES.CATEGORY({ slug: grandChild.slug });
                      return (
                        <GrandChildCategoryItem
                          key={grandChild.id}
                          grandChildCategory={grandChild}
                          href={href}
                          isNavigating={navigatingTo === href}
                          animationDelay={idx * 40}
                          onNavigate={handleNavigate}
                        />
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </CategoryIntentProvider>
  );
}

function CategoryItem({
  category,
  href,
  isHovered,
  isNavigating,
  animationDelay,
  hasChildren,
  onHover,
  onFocus,
  onNavigate,
}: {
  category: CategoriesEntity;
  href: string;
  isHovered: boolean;
  isNavigating: boolean;
  animationDelay: number;
  hasChildren: boolean;
  onHover: () => void;
  onFocus: () => void;
  onNavigate: (href: string, e: React.MouseEvent) => void;
}) {
  const touchStartedCollapsedRef = useRef(false);

  return (
    <Link
      prefetch={false}
      href={href}
      aria-expanded={hasChildren ? isHovered : undefined}
      aria-haspopup={hasChildren ? 'menu' : undefined}
      className={cn(
        'flex w-full shrink-0 items-center justify-between gap-2.5 rounded-full p-4',
        'transition-all duration-200 ease-in-out',
        'animate-in fade-in slide-in-from-top-1',
        isHovered ? 'bg-[#FFFACC]' : 'hover:bg-[#FFFACC]',
        isNavigating && 'opacity-60',
      )}
      style={{ animationDelay: `${animationDelay}ms`, animationFillMode: 'backwards' }}
      onPointerDown={event => {
        touchStartedCollapsedRef.current =
          event.pointerType === 'touch' && hasChildren && !isHovered;
      }}
      onPointerCancel={() => {
        touchStartedCollapsedRef.current = false;
      }}
      onClick={e => {
        const shouldExpand =
          touchStartedCollapsedRef.current || shouldExpandOnTouch(hasChildren, isHovered);
        touchStartedCollapsedRef.current = false;

        if (shouldExpand) {
          e.preventDefault();
          onFocus();
          return;
        }
        onNavigate(href, e);
      }}
      onMouseEnter={onHover}
      onFocus={onFocus}
    >
      {category?.icon?.url ? (
        <Image
          src={category.icon.url}
          alt={resolveImageAlt(category?.icon, category?.name)}
          width={24}
          height={24}
          className="shrink-0 object-contain"
        />
      ) : (
        <span className="size-6 shrink-0" aria-hidden />
      )}
      <span className="h6 w-full flex-1 font-semibold tracking-tight">{category.name}</span>
      {isNavigating ? (
        <AiOutlineLoading3Quarters size={16} className="shrink-0 animate-spin text-primary" />
      ) : hasChildren ? (
        <ButtonIcon
          alt={category?.name}
          url="/images/icons/arrow-down-primary.svg"
          className={cn(
            '-rotate-90 transition-transform duration-200',
            isHovered && 'translate-x-0.5',
          )}
        />
      ) : null}
    </Link>
  );
}

function SubcategoryItem({
  subCategory,
  href,
  isHovered,
  isNavigating,
  animationDelay,
  onHover,
  onFocus,
  onNavigate,
}: {
  subCategory: CategoriesEntity;
  href: string;
  isHovered: boolean;
  isNavigating: boolean;
  animationDelay: number;
  onHover: () => void;
  onFocus: () => void;
  onNavigate: (href: string, e: React.MouseEvent) => void;
}) {
  const hasChildren = hasCategoryChildren(subCategory);
  const touchStartedCollapsedRef = useRef(false);

  return (
    <Link
      prefetch={false}
      href={href}
      aria-expanded={hasChildren ? isHovered : undefined}
      aria-haspopup={hasChildren ? 'menu' : undefined}
      className={cn(
        'flex w-full shrink-0 items-center justify-between gap-2.5 rounded-full p-4',
        'transition-all duration-200 ease-in-out',
        'animate-in fade-in slide-in-from-left-1',
        isHovered ? 'bg-[#FFFACC]' : 'hover:bg-[#FFFACC]',
        isNavigating && 'opacity-60',
      )}
      style={{ animationDelay: `${animationDelay}ms`, animationFillMode: 'backwards' }}
      onPointerDown={event => {
        touchStartedCollapsedRef.current =
          event.pointerType === 'touch' && hasChildren && !isHovered;
      }}
      onPointerCancel={() => {
        touchStartedCollapsedRef.current = false;
      }}
      onClick={e => {
        const shouldExpand =
          touchStartedCollapsedRef.current || shouldExpandOnTouch(hasChildren, isHovered);
        touchStartedCollapsedRef.current = false;

        if (shouldExpand) {
          e.preventDefault();
          onFocus();
          return;
        }
        onNavigate(href, e);
      }}
      onMouseEnter={onHover}
      onFocus={onFocus}
    >
      <Image
        src={subCategory?.icon?.url ?? '/images/placeholder.png'}
        alt={resolveImageAlt(subCategory?.icon, subCategory?.name)}
        width={48}
        height={48}
        className="shrink-0 object-contain"
      />
      <span className="h6 w-full flex-1 font-semibold tracking-tight">{subCategory.name}</span>
      {isNavigating ? (
        <AiOutlineLoading3Quarters size={16} className="shrink-0 animate-spin text-primary" />
      ) : hasChildren ? (
        <ButtonIcon
          alt={subCategory.name}
          url="/images/icons/arrow-down-primary.svg"
          className={cn(
            '-rotate-90 opacity-40 transition-all duration-200',
            isHovered && 'translate-x-0.5 opacity-80',
          )}
        />
      ) : null}
    </Link>
  );
}

function GrandChildCategoryItem({
  grandChildCategory,
  href,
  isNavigating,
  animationDelay,
  onNavigate,
}: {
  grandChildCategory: CategoriesEntity;
  href: string;
  isNavigating: boolean;
  animationDelay: number;
  onNavigate: (href: string, e: React.MouseEvent) => void;
}) {
  return (
    <Link
      prefetch={false}
      href={href}
      onClick={e => onNavigate(href, e)}
      className={cn(
        'flex w-full shrink-0 items-center justify-between gap-2 rounded-full p-4',
        'text-sm font-semibold tracking-tight text-primary lg:text-base',
        'transition-all duration-200 ease-in-out hover:bg-[#FFFACC]',
        'animate-in fade-in slide-in-from-left-1',
        isNavigating && 'opacity-60',
      )}
      style={{ animationDelay: `${animationDelay}ms`, animationFillMode: 'backwards' }}
    >
      <span className="flex-1">{grandChildCategory.name}</span>
      {isNavigating && (
        <AiOutlineLoading3Quarters size={14} className="shrink-0 animate-spin text-primary" />
      )}
    </Link>
  );
}

function CategoryLoadingState({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground" role="status">
      <AiOutlineLoading3Quarters className="shrink-0 animate-spin" />
      {label}
    </div>
  );
}

function CategoryErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <button
      type="button"
      className="p-4 text-left text-sm font-semibold text-primary"
      onClick={onRetry}
    >
      Kategórie sa nepodarilo načítať. Skúsiť znova
    </button>
  );
}
